import { useState } from 'react'

import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Pencil } from '@factorialco/f0-react/icons/app'
import {
  CompetenciesDeleteCompetencyGroupDocument,
  CompetenciesEditGroupFragment,
  CompetencyGroupsDataCollectionDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EditCompetencyGroupModal } from 'modules/competencies/pages/CompetencyGroups/EditCompetencyGroupModal'

interface Props {
  canManageCompetencies: boolean
  onNewCompetencyGroup: () => void
}

export const CompetencyGroupsDataCollection = ({
  canManageCompetencies,
  onNewCompetencyGroup,
}: Props) => {
  const { openDialog } = useConfirmationDialog()
  const [competencyGroupToEdit, setCompetencyGroupToEdit] =
    useState<CompetenciesEditGroupFragment | null>(null)

  const [deleteCompetenciesCompetencyGroup] = useMutation(
    CompetenciesDeleteCompetencyGroupDocument,
    {
      extractorFn: (data) => data.competenciesMutations.deleteCompetenciesCompetencyGroup,
    }
  )

  const onDelete = async (competencyGroup: CompetenciesEditGroupFragment) => {
    const result = await deleteCompetenciesCompetencyGroup({
      variables: { id: competencyGroup.id },
      update(cache) {
        cache.evict({
          id: cache.identify({ id: competencyGroup.id, __typename: 'CompetenciesCompetencyGroup' }),
        })
      },
      refetchQueries: [CompetencyGroupsDataCollectionDocument],
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    return handled
  }

  const source = useGraphqlDataCollectionSource({
    totalItemSummary: true,
    document: CompetencyGroupsDataCollectionDocument,
    mapData: (data) => data.competencies.competencyGroupsConnection.nodes,
    mapPagination: (data) => {
      return {
        total: data.competencies.competencyGroupsConnection.totalCount ?? 0,
      }
    },
    mapVariables: ({ search, sortings }) => {
      return {
        search,
        sort: sortings?.[0],
      }
    },
    sortings: {
      name: {
        label: i18n.t('competencies_v3.competency_groups_table.columns.name'),
      },
    },
    defaultSortings: {
      field: 'name',
      order: 'asc',
    },
    search: {
      enabled: true,
    },
    primaryActions: () =>
      canManageCompetencies
        ? {
            label: i18n.t('competencies_v3.competency_groups_table.empty_state.new_button'),
            icon: Add,
            onClick: () => onNewCompetencyGroup(),
          }
        : undefined,
    itemActions: (item) =>
      canManageCompetencies
        ? [
            {
              label: i18n.t('common.edit'),
              icon: Pencil,
              onClick: () => setCompetencyGroupToEdit(item),
            },
            {
              label: i18n.t('common.delete'),
              icon: Delete,
              onClick: () =>
                openDialog({
                  illustration: 'delete-cross',
                  title: i18n.t('competencies_v3.delete_competency_group.title'),
                  description: i18n.t('competencies_v3.delete_competency_group.description'),
                  onConfirm: () => onDelete(item),
                }),
              critical: true,
            },
          ]
        : undefined,
    perPage: 20,
  })

  const visualizations = [
    {
      type: 'table' as const,
      options: {
        columns: [
          {
            label: i18n.t('competencies_v3.competency_groups_table.columns.name'),
            render: (item: { name: string }) => ({
              type: 'text' as const,
              value: item.name,
            }),
            sorting: 'name' as const,
          },
        ],
      },
    },
  ]

  return (
    <>
      <OneDataCollection
        fullHeight
        source={source}
        visualizations={visualizations}
        emptyStates={{
          'no-data': {
            title: i18n.t('competencies_v3.competency_groups_table.empty_state.title'),
            description: i18n.t('competencies_v3.competency_groups_table.empty_state.description'),
            actions: [
              {
                label: i18n.t('competencies_v3.competency_groups_table.empty_state.new_button'),
                icon: Add,
                onClick: () => onNewCompetencyGroup(),
              },
            ],
          },
        }}
      />

      {competencyGroupToEdit && (
        <EditCompetencyGroupModal
          item={competencyGroupToEdit}
          onClose={() => setCompetencyGroupToEdit(null)}
        />
      )}
    </>
  )
}
